'use client';

import { useEffect, useRef, useState } from 'react';

interface LazyVideoProps {
  src: string;
  poster: string;
  className?: string;
  // Se true, anche il poster viene scaricato solo vicino al viewport
  // (per video sotto la piega). Di default è subito visibile.
  lazyPoster?: boolean;
}

// Video decorativo (muto, in loop) che viene scaricato solo quando entra nel
// viewport. Fino ad allora mostra il poster. Se l'elemento è nascosto
// (display: none, es. su mobile) non viene mai scaricato.
export default function LazyVideo({ src, poster, className, lazyPoster = false }: LazyVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) ref.current?.play().catch(() => {});
  }, [isVisible, src]);

  return (
    <video
      ref={ref}
      src={isVisible ? src : undefined}
      poster={lazyPoster && !isVisible ? undefined : poster}
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
      className={className}
    />
  );
}
